import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useApiQuery } from "../../../hooks/use-api-query";
import type {
  AppConfig,
  TestSuite,
  TimeSeriesFilters as TimeSeriesFiltersType,
} from "../../../types";

interface TimeSeriesFiltersProps {
  value: TimeSeriesFiltersType;
  onChange: (filters: TimeSeriesFiltersType) => void;
}

export function TimeSeriesFilters({ value, onChange }: TimeSeriesFiltersProps) {
  // Fetch test suites
  const { data: testSuitesRaw, isLoading: isLoadingSuites } = useApiQuery<
    TestSuite[] | { items: TestSuite[] }
  >("/test-suites");

  const testSuites = useMemo(() => {
    if (!testSuitesRaw) return [];
    if (Array.isArray(testSuitesRaw)) return testSuitesRaw;
    return testSuitesRaw.items ?? [];
  }, [testSuitesRaw]);

  // Fetch app configs
  const { data: configsData, isLoading: isLoadingConfigs } = useApiQuery<{
    configs: AppConfig[];
  }>("/config");

  const appConfigs = useMemo(() => configsData?.configs ?? [], [configsData?.configs]);

  // Find selected items
  const selectedSuites = useMemo(
    () => testSuites.filter((s) => value.testSuiteIds?.includes(s.id)),
    [testSuites, value.testSuiteIds],
  );

  const selectedConfigs = useMemo(
    () => appConfigs.filter((c) => value.appConfigIds?.includes(c.id)),
    [appConfigs, value.appConfigIds],
  );

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "stretch", md: "center" }}
      justifyContent="flex-end"
      sx={{ mb: 2 }}
    >
      {/* Test Suite Filter */}
      <Autocomplete
        multiple
        size="small"
        sx={{ minWidth: 200, maxWidth: 300 }}
        options={testSuites}
        value={selectedSuites}
        loading={isLoadingSuites}
        getOptionLabel={(option) => option.name}
        getOptionKey={(option) => option.id}
        onChange={(_, newValue) => {
          onChange({
            ...value,
            testSuiteIds: newValue.length > 0 ? newValue.map((s) => s.id) : undefined,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Test Suites"
            placeholder="Filter by suite..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingSuites && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
          ))
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        noOptionsText="No test suites found"
        loadingText="Loading suites..."
      />

      {/* App Config Filter */}
      <Autocomplete
        multiple
        size="small"
        sx={{ minWidth: 200, maxWidth: 300 }}
        options={appConfigs}
        value={selectedConfigs}
        loading={isLoadingConfigs}
        getOptionLabel={(option) => option.name}
        getOptionKey={(option) => option.id}
        onChange={(_, newValue) => {
          onChange({
            ...value,
            appConfigIds: newValue.length > 0 ? newValue.map((c) => c.id) : undefined,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="App Configs"
            placeholder="Filter by config..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingConfigs && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
          ))
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        noOptionsText="No app configs found"
        loadingText="Loading configs..."
      />

      {/* Group By Toggle */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Group by:
        </Typography>
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => onChange({ ...value, groupByAppConfig: false })}
            variant={!value.groupByAppConfig ? "contained" : "outlined"}
          >
            None
          </Button>
          <Button
            onClick={() => onChange({ ...value, groupByAppConfig: true })}
            variant={value.groupByAppConfig ? "contained" : "outlined"}
          >
            App Config
          </Button>
        </ButtonGroup>
      </Stack>
    </Stack>
  );
}
